import styles from './webkua-table.module.scss';
import WebkuaToolBar from '../webkua-tool-bar/webkua-tool-bar';
import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import { CellFocusedEvent, GetContextMenuItemsParams, GridApi } from 'ag-grid-community';
import { DateRange } from '@mui/x-date-pickers-pro/DateRangePicker';
import { Alert, AlertTitle, BaseTable, TableConfig, TableProductsFilterProps, handleSaveTable, iTableSwitch } from '@frontend-workspace/react-shared-lib';

/* eslint-disable-next-line */
export interface WebkuaTableProps {
  data: any[],
  headers: any[],
  dateRange?: DateRange<Date>,
  supportFutureDates?: boolean,
  onSetDateRange?: (dateRange: DateRange<Date> | null) => void,
  onSelectionChanged?: (...args: any[]) => void,
  onRowClicked?: (event: any) => void;
  pageSize?: number,
  disableSearch?: boolean,
  tableFilters?: iTableSwitch[],
  tableConfig?: TableConfig,
  expandComponent?: any,
  expandProps?: any,
  productsFilter?: TableProductsFilterProps,
  isLoading?: boolean;
  showNoDataAlert?: boolean;
  onRefresh?: () => void,
  hidePagination?: boolean,
  customComponent?: any,
  exportColumnKeys?: string[],
  exportVisibleColumnsOnly?: boolean,
}

export function WebkuaTable(props: WebkuaTableProps) {
  const [quickFilter, setQuickFilter] = useState<string>("");
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  // Unmount cleanup must invalidate the API before pending effects run.
  const gridApiRef = useRef<GridApi | null>(null);
  const [summaryRow, setSummaryRow] = useState<any[]>([]);
  const [hasNoExportColumns, setHasNoExportColumns] = useState(false);

  const handleGridApiChange = useCallback((api: GridApi | null) => {
    gridApiRef.current = api;
    setGridApi(api);
  }, []);

  const getContextMenuItems = useCallback((params: GetContextMenuItemsParams) => {
    //const getContextMenuItems: any[] = ["copy", "export"];
    const getContextMenuItems: any[] = ["copy", {
      name: t("webkuaTable.exportToExcel"), action: () => {
        setHasNoExportColumns(false);
        const columnKeys = props.exportVisibleColumnsOnly
          ? params.columnApi.getAllDisplayedColumns().map(column => column.getColId())
            .filter(key => !props.exportColumnKeys || props.exportColumnKeys.includes(key))
          : props.exportColumnKeys;
        // AG Grid treats an empty columnKeys array as default column selection.
        if (props.exportVisibleColumnsOnly && columnKeys?.length === 0) {
          setHasNoExportColumns(true);
          return;
        }
        params.api.exportDataAsExcel({
          ...(columnKeys ? { columnKeys } : {}),
          processCellCallback: (param: any) => {
            //we need to check if the cell has a renderer, and if so, use it to get the value
            if (param.column.colDef.cellRenderer && typeof param.column.colDef.cellRenderer === "function" && !param.node.group) {
              //Use the renderer to get the value if possible. If not, fallback to raw value
              try {
                const updatedNode = { ...param.node, value: param.value };
                const renderedValue = param.column.colDef.cellRenderer(updatedNode);
                if (typeof renderedValue === "object") return param.value; // Fallback to raw value
                return renderedValue;
              }
              catch (e) {
                console.log("error", e, params.value);
                return param.value;
              }
            }
            return param.value; // Fallback to raw value
          }
        });
      }
    }];
    const tableConfig = props.tableConfig;
    if (tableConfig) {
      getContextMenuItems.push({
        name: t("simpleTable.saveTableConfiguration"),
        action: () => handleSaveTable(params, tableConfig)
      });
      getContextMenuItems.push({
        name: t("simpleTable.resetTableConfiguration"),
        action: () => tableConfig?.onSaveTableConfig(null, null)
      });
    }
    return getContextMenuItems;
  }, [props.exportColumnKeys, props.exportVisibleColumnsOnly, props.tableConfig]);

  const focusInputCell = useCallback((event: CellFocusedEvent) => { // If a cell containing any HTML input is highlighted, autofocus on the input
    const { column, rowIndex } = event;
    if (column && rowIndex !== undefined) {
      const columnId = column.getId();
      const cellSelector = `.ag-row[row-index="${rowIndex}"] .ag-cell[col-id="${columnId}"]`;
      const cellElement = document.querySelector(cellSelector);
      if (cellElement) {
        const inputEl = cellElement.querySelector('input') as HTMLInputElement | null;
        if (inputEl) {
          inputEl.focus();
        }
      }
    }
  }, []);

  const headers = props.headers.map(h => {
    const columnDefinition = props.tableConfig?.columns && props.tableConfig?.columns?.find(c => c.columnName === h.field);
    if (columnDefinition) {
      h.hide = columnDefinition.isHidden;
      h.sort = columnDefinition.sortDirection;
    }
    return h;
  });

  const createSummaryRow = useCallback((api: GridApi) => {
    const summaryRow: any = {};
    const filteredRows: any[] = [];

    api.forEachNodeAfterFilter(node => filteredRows.push(node.data));
    props.headers.forEach(h => {
      summaryRow[h.field] = h.summary ? h.summary(h, filteredRows) : null;
    });
    summaryRow["isSummary"] = true;
    return [summaryRow];
  }, [props.headers]);

  useEffect(() => {
    const api = gridApiRef.current;
    if (api) {
      setSummaryRow(createSummaryRow(api));
      api.setQuickFilter(quickFilter);
      if (props.tableConfig?.filterModel) api.setFilterModel(props.tableConfig.filterModel);
    }
  }, [quickFilter, gridApi, props.data, props.tableConfig, createSummaryRow]);

  const handleTableFilterChanged = () => {
    const api = gridApiRef.current;
    if (api) setSummaryRow(createSummaryRow(api));
  };

  const handleSelectionChanged = () => {
    const selectedRows = gridApiRef.current?.getSelectedRows();
    props.onSelectionChanged?.(selectedRows);
  };

  return (<>
    {hasNoExportColumns && <Alert severity="warning" className='my-3'>
      {t("webkuaTable.noVisibleExportColumns")}
    </Alert>}
    <WebkuaToolBar
      quickFilter={quickFilter}
      setQuickFilter={setQuickFilter}
      dateRange={props.dateRange}
      disableSearch={props.disableSearch}
      onRefresh={props.onRefresh}
      tableFilters={props.tableFilters}
      productsFilter={props.productsFilter}
      supportFutureDates={props.supportFutureDates}
      onSetDateRange={props.onSetDateRange}
      customComponent={props.customComponent}
    />
    {props.showNoDataAlert ? <Alert severity="info" className='my-3'>
      <AlertTitle>{t("webkuaChart.noDataInTheSearch")}</AlertTitle>
      <div className='mb-1'>
        {t("webkuaChart.selectProductsTimePeriodEtcAndRefreshTableToRetrieveData")}
      </div>
      <div className='mb-1'>
        {t("webkuaChart.ifYouHaveMadeChangesToSearchCriteriaEtc")}
      </div>
      <div className='mb-1'>
        {t("webkuaChart.emptyProductListCompanySelectionSelectsAllProductsCompanies")}
      </div>
    </Alert> : <BaseTable
      data={props.data}
      getContextMenuItems={getContextMenuItems}
      headers={headers}
      dateRange={props.dateRange}
      pageSize={props.pageSize || 15}
      expandComponent={props.expandComponent}
      isLoading={props.isLoading}
      gridApi={gridApi}
      onTableFilterChanged={handleTableFilterChanged}
      singleCellSelection={!props.onSelectionChanged}
      summaryRow={summaryRow}
      setGridApi={handleGridApiChange}
      expandProps={props.expandProps}
      onSelectionChanged={handleSelectionChanged}
      onRowClicked={props.onRowClicked}
      onCellFocused={focusInputCell}
    />}
  </>);
}

export default WebkuaTable;
